<template>
  <div class="onboarding-info-page">
    <div class="onboarding-info-page__top">
      <div class="onboarding-info-page__context" v-html="contextTitle"></div>

      <div class="onboarding-info-progress">
        <div class="onboarding-info-progress__bar"
             :class="{ 'onboarding-info-progress__bar--complete': internalStep === 2 }">
          <span></span>
          <span></span>
        </div>
        <div class="onboarding-info-progress__labels">
          <span :class="{ 'onboarding-info-progress__label--active': internalStep < 2 }">
            {{ questionOneLabel }}
          </span>
          <span :class="{ 'onboarding-info-progress__label--active': internalStep === 2 }">
            {{ questionThreeLabel }}
          </span>
        </div>
      </div>
    </div>

    <div v-if="internalStep === 0" class="onboarding-info-page__intro">
      <h1 class="onboarding-info-page__title" v-html="introTitle"></h1>
      <p class="onboarding-info-page__text" v-html="introText"></p>
      <p class="onboarding-info-page__text" v-html="introReadyText"></p>
    </div>

    <div v-if="internalStep === 1" class="onboarding-info-page__questions">
      <section class="onboarding-info-question onboarding-info-question--service">
        <h1 class="onboarding-info-page__title" v-html="serviceQuestion"></h1>
        <div class="onboarding-info-page__choices onboarding-info-page__choices--three">
          <OnboardingRadioCard v-for="option in serviceOptions"
                               :key="option.value"
                               :value="option.value"
                               :model-value="serviceType"
                               :items="option.items"
                               @select="setServiceType" />
        </div>
      </section>

      <section class="onboarding-info-question">
        <h2 class="onboarding-info-page__title" v-html="employeeQuestion"></h2>
        <div class="onboarding-info-page__choices onboarding-info-page__choices--two">
          <OnboardingRadioCard v-for="option in employeeOptions"
                               :key="option.value"
                               :value="option.value"
                               :model-value="salarieType"
                               :title="option.title"
                               @select="setSalarieType" />
        </div>
      </section>
    </div>

    <div v-if="internalStep === 2" class="onboarding-info-page__questions">
      <section class="onboarding-info-question">
        <h1 class="onboarding-info-page__title" v-html="paieCountQuestion"></h1>
        <div class="onboarding-info-page__choices onboarding-info-page__choices--three">
          <OnboardingRadioCard v-for="option in paieCountOptions"
                               :key="option.value"
                               :value="option.value"
                               :model-value="nbPaiesType"
                               :title="option.title"
                               @select="setNbPaiesType" />
        </div>
      </section>
    </div>
  </div>

  <div class="action-footer onboarding-info-page__footer">
    <loader-button :label="precedentBtnTxt"
                   :outlined="true"
                   @callback="previous" />
    <loader-button :label="nextButtonLabel"
                   :disabled="nextDisabled"
                   @callback="next" />
  </div>
</template>

<script lang="ts">
  import { defineComponent, type PropType } from "vue";
  import LoaderButton from "@/framework/base/components/LoaderButton.vue";
  import {
    AppButtons,
    AppCustoms,
    RessourceAppButton,
    RessourceAppCustom
  } from "@/ressources/globalRessources.ts";
  import Synchro98, {
    Synchro98NbPaiesTypes,
    Synchro98SalarieTypes,
    Synchro98ServiceTypes
  } from "@/generated/classes/Synchro98";
  import OnboardingRadioCard from "@/components/Onboarding/OnboardingRadioCard.vue";
  import { storeEtablissement, storeSession } from '@/generated/stores';

  interface RadioOption {
    value: number;
    title?: string;
    items?: string[];
  }

  export default defineComponent({
    name: "SectionOnboardingAbo",
    components: {
      LoaderButton,
      OnboardingRadioCard
    },
    props: {
      synchro98: {
        type: Object as PropType<Synchro98>,
        required: false,
      },
      selectedServiceType: {
        type: Number,
        default: Synchro98ServiceTypes.ASSISTED,
      },
      selectedSalarieType: {
        type: Number,
        default: Synchro98SalarieTypes.INTERMITTENTS,
      },
      selectedNbPaiesType: {
        type: Number,
        default: Synchro98NbPaiesTypes.MEDIUM,
      },
    },
    emits: ["next", "previous", "updateServiceType", "updateSalarieType", "updateNbPaiesType"],
    data() {
      return {
        internalStep: 0
      };
    },
    computed: {
      serviceType(): Synchro98ServiceTypes {
        return this.selectedServiceType as Synchro98ServiceTypes;
      },
      salarieType(): Synchro98SalarieTypes {
        return this.selectedSalarieType as Synchro98SalarieTypes;
      },
      nbPaiesType(): Synchro98NbPaiesTypes {
        return this.selectedNbPaiesType as Synchro98NbPaiesTypes;
      },
      establishmentName(): string {
        const etablissementId = this.synchro98?.etablissementId ?? 0;
        if (etablissementId > 0) {
          const etablissement = storeEtablissement.getEtablissement(etablissementId);
          return etablissement.alias || etablissement.nom || "Nom de l'\u00e9tablissement";
        }
        return "Nom de l'\u00e9tablissement";
      },
      contextTitle(): string {
        return RessourceAppCustom(AppCustoms.ONBOARDING_INFO_CONTEXT).label.replace("{0}", this.establishmentName);
      },
      questionOneLabel(): string {
        return RessourceAppCustom(AppCustoms.ONBOARDING_INFO_QUESTIONS_ONE_TWO).label;
      },
      questionThreeLabel(): string {
        return RessourceAppCustom(AppCustoms.ONBOARDING_INFO_QUESTION_THREE).label;
      },
      introTitle(): string {
        return RessourceAppCustom(AppCustoms.ONBOARDING_INFO_INTRO_TITLE).label;
      },
      introText(): string {
        return RessourceAppCustom(AppCustoms.ONBOARDING_INFO_INTRO).label;
      },
      introReadyText(): string {
        return RessourceAppCustom(AppCustoms.ONBOARDING_INFO_READY).label;
      },
      serviceQuestion(): string {
        return RessourceAppCustom(AppCustoms.ONBOARDING_INFO_SERVICE_QUESTION).label;
      },
      employeeQuestion(): string {
        return RessourceAppCustom(AppCustoms.ONBOARDING_INFO_EMPLOYEE_QUESTION).label;
      },
      paieCountQuestion(): string {
        return RessourceAppCustom(AppCustoms.ONBOARDING_INFO_PAIE_COUNT_QUESTION).label;
      },
      serviceOptions(): RadioOption[] {
        return [
          {
            value: Synchro98ServiceTypes.ESSENTIAL,
            items: [
              RessourceAppCustom(AppCustoms.ONBOARDING_INFO_SERVICE_TCHAT).label,
              RessourceAppCustom(AppCustoms.ONBOARDING_INFO_SERVICE_CALLBACK).label
            ]
          },
          {
            value: Synchro98ServiceTypes.ASSISTED,
            items: [
              RessourceAppCustom(AppCustoms.ONBOARDING_INFO_SERVICE_TCHAT).label,
              RessourceAppCustom(AppCustoms.ONBOARDING_INFO_SERVICE_CALLBACK).label,
              RessourceAppCustom(AppCustoms.ONBOARDING_INFO_SERVICE_HOTLINE).label
            ]
          },
          {
            value: Synchro98ServiceTypes.PREMIUM,
            items: [
              RessourceAppCustom(AppCustoms.ONBOARDING_INFO_SERVICE_TCHAT).label,
              RessourceAppCustom(AppCustoms.ONBOARDING_INFO_SERVICE_CALLBACK).label,
              RessourceAppCustom(AppCustoms.ONBOARDING_INFO_SERVICE_HOTLINE).label,
              RessourceAppCustom(AppCustoms.ONBOARDING_INFO_SERVICE_MANAGER).label
            ]
          }
        ];
      },
      employeeOptions(): RadioOption[] {
        return [
          {
            value: Synchro98SalarieTypes.MIXTED,
            title: RessourceAppCustom(AppCustoms.ONBOARDING_INFO_EMPLOYEE_MIXED).label
          },
          {
            value: Synchro98SalarieTypes.INTERMITTENTS,
            title: RessourceAppCustom(AppCustoms.ONBOARDING_INFO_EMPLOYEE_INTERMITTENTS).label
          }
        ];
      },
      paieCountOptions(): RadioOption[] {
        return [
          {
            value: Synchro98NbPaiesTypes.LOW,
            title: RessourceAppCustom(AppCustoms.ONBOARDING_INFO_PAIE_COUNT_LOW).label
          },
          {
            value: Synchro98NbPaiesTypes.MEDIUM,
            title: RessourceAppCustom(AppCustoms.ONBOARDING_INFO_PAIE_COUNT_MEDIUM).label
          },
          {
            value: Synchro98NbPaiesTypes.HIGHT,
            title: RessourceAppCustom(AppCustoms.ONBOARDING_INFO_PAIE_COUNT_HIGH).label
          }
        ];
      },
      nextButtonLabel(): string {
        if (this.internalStep === 0) {
          return RessourceAppCustom(AppCustoms.ONBOARDING_INFO_START_BUTTON).label;
        }
        if (this.internalStep === 2) {
          return RessourceAppCustom(AppCustoms.ONBOARDING_INFO_SUGGESTION_BUTTON).label;
        }
        return RessourceAppButton(AppButtons.SUIVANT).label;
      },
      precedentBtnTxt(): string {
        return RessourceAppButton(AppButtons.PRECEDENT).label;
      },
      nextDisabled(): boolean {
        if (this.internalStep === 1) {
          return this.serviceType === Synchro98ServiceTypes.NONE || this.salarieType === Synchro98SalarieTypes.NONE;
        }
        if (this.internalStep === 2) {
          return this.nbPaiesType === Synchro98NbPaiesTypes.NONE;
        }
        return false;
      }
    },
    methods: {
      setServiceType(value: string | number): void {
        const serviceType = Number(value) as Synchro98ServiceTypes;
        if (this.synchro98) {
          this.synchro98.serviceType = serviceType;
        }
        this.$emit("updateServiceType", serviceType);
      },
      setSalarieType(value: string | number): void {
        const salarieType = Number(value) as Synchro98SalarieTypes;
        if (this.synchro98) {
          this.synchro98.salarieType = salarieType;
        }
        this.$emit("updateSalarieType", salarieType);
      },
      setNbPaiesType(value: string | number): void {
        const nbPaiesType = Number(value) as Synchro98NbPaiesTypes;
        if (this.synchro98) {
          this.synchro98.nbPaiesType = nbPaiesType;
        }
        this.$emit("updateNbPaiesType", nbPaiesType);
      },
      previous(): void {
        if (this.internalStep === 0) {
          this.$emit("previous");
        } else {
          this.internalStep -= 1;
        }
      },
      next(): void {
        if (this.internalStep < 2) {
          this.internalStep += 1;
        } else {
          if (this.synchro98) {
            this.synchro98.serviceType = this.selectedServiceType;
            this.synchro98.salarieType = this.selectedSalarieType;
            this.synchro98.nbPaiesType = this.selectedNbPaiesType;
            Synchro98.setSubscription(this.synchro98).then((res) => {
              storeSession.clearData();
              storeSession.setUserData(res as any);
              this.$emit("next", this.synchro98);
            });
          }
        }
      }
    },
  });
</script>

<style scoped>
  .onboarding-info-page {
    width: 640px;
    padding-top: 10px;
    padding-bottom: 128px;
  }

  .onboarding-info-page__top {
    margin-bottom: 58px;
  }

  .onboarding-info-page__context {
    margin-bottom: 18px;
    color: rgb(var(--v-theme-secondary20, 19, 82, 83));
    font-size: 15px;
    font-weight: 600;
    line-height: 1.2;
  }

  .onboarding-info-progress__bar {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

    .onboarding-info-progress__bar span {
      height: 4px;
      border-radius: 999px;
      background: #cfeeff;
    }

      .onboarding-info-progress__bar span:first-child,
      .onboarding-info-progress__bar--complete span {
        background: #1597e5;
      }

  .onboarding-info-progress__labels {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 6px;
    color: rgb(var(--v-theme-grey70, 168, 174, 180));
    font-size: 11px;
    font-weight: 400;
  }

  .onboarding-info-progress__label--active {
    color: rgb(var(--v-theme-grey30, 68, 76, 84));
  }

  .onboarding-info-page__intro {
    max-width: 560px;
  }

  .onboarding-info-page__questions {
    display: flex;
    flex-direction: column;
    gap: 48px;
  }

  .onboarding-info-question {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  .onboarding-info-page__title {
    max-width: 590px;
    margin: 0;
    color: rgb(var(--v-theme-grey20, 47, 54, 61));
    font-size: 24px;
    font-weight: 700;
    line-height: 1.08;
  }

  .onboarding-info-page__text {
    max-width: 560px;
    margin: 0 0 28px;
    color: rgb(var(--v-theme-secondary20, 19, 82, 83));
    font-size: 16px;
    font-weight: 400;
    line-height: 1.55;
  }

  .onboarding-info-page__intro .onboarding-info-page__title {
    margin-bottom: 42px;
  }

  .onboarding-info-page__choices {
    display: grid;
    gap: 6px;
  }

  .onboarding-info-page__choices--three {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .onboarding-info-page__choices--two {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .onboarding-info-page__choices--three :deep(.onboarding-radio-card) {
    min-height: 92px;
  }

  .onboarding-info-question--service .onboarding-info-page__choices--three :deep(.onboarding-radio-card) {
    min-height: 206px;
  }

  .onboarding-info-page__choices--two :deep(.onboarding-radio-card) {
    min-height: 92px;
  }

  .onboarding-info-page__footer {
    gap: 8px;
  }

    .onboarding-info-page__footer :deep(.v-btn) {
      width: 262px;
    }

    .onboarding-info-page__footer :deep(.v-btn:not(.v-btn--variant-outlined)) {
      background: #ef1d1d;
    }

  @media (max-width: 960px) {
    .onboarding-info-page {
      width: 100%;
    }
  }

  @media (max-width: 700px) {
    .onboarding-info-page {
      padding-bottom: 32px;
    }

    .onboarding-info-page__choices--three,
    .onboarding-info-page__choices--two {
      grid-template-columns: 1fr;
    }

    .onboarding-info-page__footer :deep(.v-btn) {
      width: 100%;
    }
  }
</style>
